import constants, { ACCESS_TOKEN, API_RESPONSE, HTTP_METHODS } from '../../../settings/shared/constants';
import axiosClient from '../../common';
import PolicyStorage from '../llmProxy/policyStorage';
import { buildPolicyPayload } from '../../../policy/utils/policyEngine';
import UtilService from '../Utils';

const POLICY_PAGE_SIZE = 50;

function _constructHeader() {
  return {
    'Content-Type': 'application/json',
    Authorization: 'Bearer ' + localStorage.getItem(ACCESS_TOKEN),
  };
}

function getMetadata() {
  return { useLocalMetadata: true };
}

function getLLMGatewayList() {
  return { policyDTOPage: { content: PolicyStorage.getCustomPolicies() } };
}

function getCategories(page = 0, size = POLICY_PAGE_SIZE) {
  return axiosClient({
    url: `${constants.api.policy.categories}?page=${page}&size=${size}`,
    method: HTTP_METHODS.GET,
    headers: _constructHeader(),
  });
}

function getSubcategories(category, page = 0, size = POLICY_PAGE_SIZE) {
  const safeCategory = encodeURIComponent(String(category || '').trim());
  return axiosClient({
    url: `${constants.api.policy.list}/subcategories?category=${safeCategory}&page=${page}&size=${size}`,
    method: HTTP_METHODS.GET,
    headers: _constructHeader(),
  });
}

function getPolicies(page = 0, size = POLICY_PAGE_SIZE, filters = {}) {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
  });

  const category = filters.category ? String(filters.category).trim() : '';
  const subcategory = filters.subcategory ? String(filters.subcategory).trim() : '';

  if (category) params.set('category', category);
  if (subcategory) params.set('subcategory', subcategory);

  return axiosClient({
    url: `${constants.api.policy.list}?${params.toString()}`,
    method: HTTP_METHODS.GET,
    headers: _constructHeader(),
  });
}

function getPolicy(policyId) {
  const safeId = encodeURIComponent(String(policyId || '').trim());
  return axiosClient({
    url: `${constants.api.policy.get}${safeId}`,
    method: HTTP_METHODS.GET,
    headers: _constructHeader(),
  });
}

function isPolicyDocument(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  if ('content' in value && 'pageable' in value) return false;
  return Boolean(
    value.statements
    || value.version
    || value.sid
    || value.effect
    || value.action,
  );
}

function parseMaybeJson(value) {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return { _raw: trimmed };
  }
}

function unwrapPolicyDocument(res) {
  if (res == null) return null;

  if (typeof res === 'string') {
    return parseMaybeJson(res);
  }

  if (typeof res !== 'object') return { value: res };

  const nested = res.policyDocument
    ?? res.policy
    ?? res.policyDTO?.policy
    ?? res.policyDTO?.policyDocument
    ?? res.policyResponseDTO?.policy
    ?? res.policyResponseDTO?.policyDocument
    ?? res.data
    ?? res.body
    ?? res.content;

  const parsedNested = parseMaybeJson(nested);
  if (isPolicyDocument(parsedNested)) return parsedNested;

  if (isPolicyDocument(res)) return res;
  return parsedNested && typeof parsedNested === 'object' ? parsedNested : null;
}

function getInlinePolicyDocument(policy) {
  if (!policy || typeof policy !== 'object') return null;
  const candidates = [
    policy.policyDocument,
    policy.policy,
    policy.policyDTO?.policy,
    policy.policyDTO?.policyDocument,
    policy.policyResponseDTO?.policy,
    policy.policyResponseDTO?.policyDocument,
  ];
  for (const candidate of candidates) {
    const parsed = parseMaybeJson(candidate);
    if (isPolicyDocument(parsed)) return parsed;
  }
  return null;
}

function normalizePolicyPage(res, pageSize = POLICY_PAGE_SIZE) {
  const page = res?.policyResponseDTOPage
    || res?.policyDTOPage
    || res?.data
    || res;
  const content = Array.isArray(page?.content)
    ? page.content
    : Array.isArray(res?.content)
      ? res.content
      : Array.isArray(page)
        ? page
        : [];
  const totalElements = Number(page?.totalElements ?? res?.totalElements ?? content.length) || 0;
  const size = Number(page?.size ?? res?.size ?? pageSize) || pageSize;
  let totalPages = Number(page?.totalPages ?? res?.totalPages ?? 0) || 0;
  if (!totalPages && totalElements > 0) {
    totalPages = Math.max(1, Math.ceil(totalElements / size));
  }
  return { content, totalPages, totalElements, size };
}

function getPolicyS3Path(policy) {
  return String(policy?.s3Path || policy?.s3_path || policy?.path || '').trim();
}

async function resolvePolicyDocument(policy) {
  const s3Path = getPolicyS3Path(policy);
  if (s3Path) {
    const res = await UtilService.getPolicyByPath(s3Path);
    if (res?.status === API_RESPONSE.FAILED) {
      throw new Error(res?.errorMessage || 'Failed to load policy document');
    }
    const fromS3 = unwrapPolicyDocument(res);
    if (fromS3) return fromS3;
    throw new Error('Policy document was empty');
  }

  return getInlinePolicyDocument(policy);
}

function createPolicy(formValues, meta = {}) {
  const payload = buildPolicyPayload(formValues, meta);
  const entry = PolicyStorage.saveCustomPolicy({
    id: meta.id || `custom-${Date.now()}`,
    label: payload.label,
    description: payload.description,
    categoryId: payload.categoryId,
    conditionTemplate: payload.conditionTemplate,
    formType: payload.conditionTemplate,
    defaults: payload.formValues,
    policyDocument: payload.policyDocument,
    createdDate: meta.createdDate,
  });
  return { policyDTO: entry };
}

function updatePolicy(id, formValues, meta = {}) {
  const payload = buildPolicyPayload(formValues, { ...meta, id });
  const entry = PolicyStorage.saveCustomPolicy({
    id,
    label: payload.label,
    description: payload.description,
    categoryId: payload.categoryId,
    conditionTemplate: payload.conditionTemplate,
    formType: payload.conditionTemplate,
    defaults: payload.formValues,
    policyDocument: payload.policyDocument,
    createdDate: meta.createdDate,
  });
  return { policyDTO: entry };
}

function deletePolicy(id) {
  const safeId = encodeURIComponent(String(id || '').trim());
  return axiosClient({
    url: `${constants.api.policy.delete}${safeId}`,
    method: HTTP_METHODS.DELETE,
    headers: _constructHeader(),
  }).catch(() => {
    PolicyStorage.deleteCustomPolicy(id);
    return {};
  });
}

function previewPolicy(formValues) {
  const payload = buildPolicyPayload(formValues);
  return { policyDocument: payload.policyDocument };
}

const PolicyService = {
  POLICY_PAGE_SIZE,
  getMetadata,
  getLLMGatewayList,
  getCategories,
  getSubcategories,
  getPolicies,
  getPolicy,
  createPolicy,
  updatePolicy,
  deletePolicy,
  previewPolicy,
  resolvePolicyDocument,
  getInlinePolicyDocument,
  getPolicyS3Path,
  unwrapPolicyDocument,
  normalizePolicyPage,
};

export default PolicyService;
